import { useQuery } from "@tanstack/react-query";
import logoBlack from "@assets/TORK_Logo_Black_1779647609157.png";
import logoWhite from "@assets/TORK_Logo_White_1779647609157.png";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Star, BarChart3, Zap, MapPin, Shield, ExternalLink } from "lucide-react";
import { useRoute } from "wouter";

interface ShareData {
  name: string;
  city: string;
  category: string;
  avgRating: number | null;
  totalReviews: number | null;
  tokenBalance: number;
  coveragePercent: number | null;
}

export default function SharePage() {
  const [, params] = useRoute("/share/:token");
  const token = params?.token;

  const { data, isLoading, isError } = useQuery<ShareData>({
    queryKey: ["/api/share", token],
    enabled: !!token,
  });

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <div className="w-full max-w-md space-y-4">
          <Skeleton className="h-12 w-3/4 mx-auto" />
          <Skeleton className="h-64" />
        </div>
      </div>
    );
  }

  if (isError || !data) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardContent className="py-12 text-center">
            <div className="w-16 h-16 rounded-full bg-muted flex items-center justify-center mx-auto mb-4">
              <Shield className="w-8 h-8 text-muted-foreground" />
            </div>
            <h2 className="text-lg font-bold">Page Not Found</h2>
            <p className="text-sm text-muted-foreground mt-2">
              This share link is invalid or has expired.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background flex flex-col" data-testid="page-share">
      <div className="flex-1 flex items-center justify-center p-4">
        <div className="w-full max-w-md space-y-6">
          <div className="text-center space-y-2">
            <img src={logoBlack} alt="Tork" className="h-36 w-auto mx-auto block dark:hidden" data-testid="img-logo" />
            <img src={logoWhite} alt="Tork" className="h-36 w-auto mx-auto hidden dark:block" />
            <h1 className="text-2xl font-bold tracking-tight" data-testid="text-share-name">
              {data.name}
            </h1>
            <div className="flex items-center justify-center gap-2 text-sm text-muted-foreground">
              <span>{data.city}</span>
              <span>·</span>
              <Badge variant="outline" className="text-xs">{data.category}</Badge>
            </div>
          </div>

          <Card>
            <CardContent className="p-6">
              <div className="grid grid-cols-3 gap-4 text-center">
                <div className="space-y-2">
                  <div className="w-12 h-12 rounded-xl bg-brand-blue/10 flex items-center justify-center mx-auto">
                    <BarChart3 className="w-6 h-6 text-brand-blue" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold" data-testid="text-share-coverage">
                      {data.coveragePercent != null ? `${data.coveragePercent}%` : "--"}
                    </p>
                    <p className="text-xs text-muted-foreground">Coverage</p>
                  </div>
                </div>

                <div className="space-y-2">
                  <div className="w-12 h-12 rounded-xl bg-amber-500/10 flex items-center justify-center mx-auto">
                    <Star className="w-6 h-6 text-amber-400 fill-amber-400" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold" data-testid="text-share-rating">
                      {data.avgRating?.toFixed(1) || "--"}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {data.totalReviews ? `${data.totalReviews} reviews` : "Rating"}
                    </p>
                  </div>
                </div>

                <div className="space-y-2">
                  <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center mx-auto">
                    <Zap className="w-6 h-6 text-primary" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold" data-testid="text-share-tokens">
                      {data.tokenBalance}
                    </p>
                    <p className="text-xs text-muted-foreground">Tokens</p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          <div className="text-center space-y-3">
            <div className="flex items-center justify-center gap-2 text-muted-foreground">
              <div className="w-6 h-6 rounded bg-brand-orange flex items-center justify-center">
                <span className="text-white font-bold text-xs">DD</span>
              </div>
              <span className="text-sm font-medium">Powered by Digital Domination</span>
            </div>
            <a
              href="/dashboard"
              className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground transition-colors"
              data-testid="link-learn-more"
            >
              Learn more about Digital Domination
              <ExternalLink className="w-3 h-3" />
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}
